import type { CategoryFacetValue } from '@coveo/atomic-react';
import type { CategoryMetadata } from '@lib/types/category';
import type { TreeNodeData } from 'ui-components';

type BuilderNode = {
  id: string;
  label: string;
  count: number;
  disabled: boolean;
  metadata: CategoryMetadata;
  children: Map<string, BuilderNode>;
};

/** Reconstructs a nested tree from flat Coveo category values (e.g., "3d,props"). */
export function buildCategoryHierarchy(
  flatValues: CategoryFacetValue[],
  delimiter = ','
): TreeNodeData<CategoryMetadata>[] {
  const root = new Map<string, BuilderNode>();

  for (const value of flatValues) {
    const segments = value.value.split(delimiter).map((s) => s.trim());
    let currentLevel = root;
    let currentPath: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      currentPath = [...currentPath, segment];

      if (!currentLevel.has(segment)) {
        currentLevel.set(segment, {
          id: segment,
          label: segment,
          count: value.numberOfResults,
          disabled: value.numberOfResults === 0,
          metadata: {
            coveoValue: value.value,
            parentPath: currentPath.slice(0, -1),
            fullPath: [...currentPath],
            resultCount: value.numberOfResults,
          },
          children: new Map(),
        });
      } else if (i === segments.length - 1) {
        const existingNode = currentLevel.get(segment)!;
        existingNode.count = value.numberOfResults;
        existingNode.disabled = value.numberOfResults === 0;
        existingNode.metadata.resultCount = value.numberOfResults;
        existingNode.metadata.coveoValue = value.value;
      }

      if (i < segments.length - 1) {
        currentLevel = currentLevel.get(segment)!.children;
      }
    }
  }

  return convertToTreeNodeData(root);
}

function convertToTreeNodeData(
  map: Map<string, BuilderNode>
): TreeNodeData<CategoryMetadata>[] {
  return Array.from(map.values()).map((node) => {
    const children = convertToTreeNodeData(node.children);

    return {
      id: node.id,
      label: node.label,
      count: node.count,
      disabled: node.disabled,
      metadata: node.metadata,
      ...(children.length > 0 && { children }),
    };
  });
}
